import type { OrderOfferView, ShipperHomeView } from '@khaddi/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Seconds a shipper has to decide before the offer card moves on to the next order. */
export const OFFER_SECONDS = 30;

/**
 * Client-side offer rotation: the API always returns the nearest open order first, so
 * skipping or timing out is remembered locally and the next order from the queue is promoted.
 */
export const useOfferDeck = (home: ShipperHomeView | null) => {
  const offers = useMemo<OrderOfferView[]>(() => (home ? [home.offer, ...home.queue].filter((o): o is OrderOfferView => o !== null) : []), [home]);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  const [focusId, setFocusId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const shownAt = useRef(new Map<string, number>());

  const visible = offers.filter((o) => !dismissed.has(o.orderId));
  const current = visible.find((o) => o.orderId === focusId) ?? visible[0] ?? null;
  const currentId = current?.orderId ?? null;

  useEffect(() => {
    if (!currentId) return;
    if (!shownAt.current.has(currentId)) shownAt.current.set(currentId, Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [currentId]);

  const startedAt = currentId ? (shownAt.current.get(currentId) ?? now) : now;
  const secondsLeft = Math.max(0, OFFER_SECONDS - Math.floor((now - startedAt) / 1000));

  const dismiss = useCallback((orderId: string) => {
    setDismissed((prev) => new Set(prev).add(orderId));
    setFocusId(null);
  }, []);

  useEffect(() => {
    if (currentId && secondsLeft === 0) dismiss(currentId);
  }, [currentId, secondsLeft, dismiss]);

  const currentRef = useRef(currentId);
  currentRef.current = currentId;

  const promote = useCallback((orderId: string) => {
    const at = Date.now();
    // the card being replaced goes back to the queue and gets a fresh countdown when it returns
    if (currentRef.current) shownAt.current.delete(currentRef.current);
    shownAt.current.set(orderId, at);
    setNow(at);
    setFocusId(orderId);
  }, []);

  const restore = useCallback(() => {
    shownAt.current.clear();
    setNow(Date.now());
    setDismissed(new Set());
  }, []);

  return {
    current,
    secondsLeft,
    queue: visible.filter((o) => o.orderId !== currentId),
    skippedCount: offers.length - visible.length,
    dismiss,
    promote,
    restore,
  };
};
